(async () => {
  if (!customElements.get('product-upsell-swiper')) {
    const { Swiper } = await import('swiper');
    const { Navigation, A11y, Pagination } = await import('swiper/modules');

    Swiper.use([Navigation, A11y, Pagination]);

    class ProductUpsellSwiper extends HTMLElement {
      constructor() {
        super();

        this.contentNav = document.querySelector('product-content-navigation');

        this.options = {
          showSwiper: false,
          productHandles: [],
          colorScheme: 'color-scheme--gray',
          atcBehavior: 'update_cart',
          totalSlides: 8, // not a swiper option, but added for 'recently viewed products' which is based on a an array of visited products
        };

        this.swiperOptions = {
          a11y: true,
          observer: true,
          threshold: 10,
          slidesPerView: 1,
          navigation: {
            nextEl: '.cart-drawer-upsell-next',
            prevEl: '.cart-drawer-upsell-prev',
          },
        };

        // Check if we have extra options on the HTML
        if (this && this.dataset && this.dataset.options) {
          const jsonOptions = JSON.parse(this.dataset.options);
          if (jsonOptions) {
            this.options = {
              ...this.options,
              ...jsonOptions,
            };
          }
        }

        this.init();
      }

      /**
       * Init the Variant slider and the upsell slider
       * Options should have 2 items:
       * "productHandles": string, comma separated,
       * "colorScheme": color scheme for the product cards, either 'color-scheme--gray' or 'color-scheme--white
       */
      init() {
        this.productsArray = this.options.productHandles.split(',');
        if (this.productsArray.length === 1 && this.productsArray[0] === '') {
          this.closest('.product-carousel')?.classList.add('hidden');
          return;
        }

        this.swiper = this.closest('.product-carousel');

        // cut the array to the max amount of slides
        if (this.productsArray.length > this.options.totalSlides) {
          this.productsArray.length = this.options.totalSlides;
        }

        this.fetchProducts();
      }

      async fetchProducts() {
        this.querySelector('[data-product-card-wrapper]').innerHTML = '';

        // use map instead of forEach because async/await won't work
        const promises = this.productsArray.map(async (product_handle) => {
          await fetch(
            `/products/${product_handle}?view=product-card&color-scheme=${this.options.colorScheme}&atc-behavior=${this.options.atcBehavior}`,
          )
            .then((response) => response.text())
            .then((responseText) => {
              const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
              const productHTML = parsedHTML.getElementById('product-card-slide');
              if (productHTML) {
                // appending the slide will automatically trigger the class and init the product card swiper
                this.querySelector('[data-product-card-wrapper]').append(productHTML);
              }
            })
            .catch((error) => {
              debug() && console.log(error);
            });
        });

        // init the main swiper when all items have been processed
        await Promise.allSettled(promises).then(() => {
          // show the block
          if (this.closest('.product-content-block')) {
            this.closest('.product-content-block').classList.remove('hidden');
          }

          // check if there are slides, else don't do anything
          if (this.querySelector('[data-product-card-wrapper]').innerHTML !== '') {
            if (this.swiper) {
              // the cart-upsell is fetched, so we need to re-init the swiper
              this.swiper.init(this.swiper.querySelector('[data-default-swiper]'));
              this.swiper.classList.remove('hidden');
            } else if (this.closest('.cart-upsell')) {
              this.closest('.cart-upsell')?.classList.remove('hidden');
            } else if (this.closest('.cart-notification-upsell')) {
              this.closest('.cart-notification-upsell')?.classList.remove('hidden');
            }
          } else {
            if (this.closest('.product-carousel')) {
              this.closest('.product-carousel')?.classList.add('hidden');
            }
          }

          // Create a swiper in the cart drawer and on the cart page
          if (this.options.showSwiper) {
            this.swiperInstance = new Swiper(this, this.swiperOptions);
          }

          // PDP only
          if (this.contentNav) {
            this.updateNav();
          }

          // on the PDP dispatch an event so we know when the swiper is done rendering
          if (this.closest('product-content')) {
            // dispatch a window event
            setTimeout(() => {
              window.dispatchEvent(new Event('upsell-swiper-loaded', { bubbles: true }));
            }, 500);
          }

          // update the reviews
          if (window.apps.reviewsIo) getRating();
        });
      }

      updateNav() {
        // Show the anchor navigation link
        const input = this.contentNav.querySelector('input#nav-block-upsell');

        if (!input) return;

        input.removeAttribute('disabled');
        input.classList.remove('hidden');
        input.nextElementSibling.classList.remove('hidden');
        input.closest('li').classList.remove('hidden');
      }
    }

    customElements.define('product-upsell-swiper', ProductUpsellSwiper);
    window.ProductUpsellSwiper = ProductUpsellSwiper;
  }
})();
